import classnames from "classnames";
import * as PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { CloseButton } from "../generic/CloseButton";

export const DropdownMenuTheme = Object.freeze({
  BLUE: "blue",
  LIGHT_BORDERLESS: "light_borderless",
  ORANGE: "orange",
  DARK: "dark",
});

export const DropdownMenuAlign = Object.freeze({
  RIGHT: "right",
  LEFT: "left",
});

/**
 * Sadly React would complain with "Function components cannot be given refs"
 * warnings if we would write this as a pure component. It is used in
 * `Dropdown.Trigger` as a custom component.
 */
export const DropdownTrigger = React.forwardRef(
  ({ children, theme, className, arrowClass, ...rest }, ref) => {
    // eslint-disable-line no-unused-vars
    const showMore = !children[0] ? `icon-show-more--${theme}` : "";
    return (
      <button
        type="button"
        aria-haspopup
        aria-expanded={false}
        data-testid="dropdown-trigger"
        {...rest}
        className={classnames(
          className,
          `dropdown-toggle btn btn--${theme}`,
          showMore,
          arrowClass
        )}
        style={{ height: "100%" }}
      >
        {children}
      </button>
    );
  }
);

DropdownTrigger.propTypes = {
  arrowClass: PropTypes.string.isRequired,
  theme: PropTypes.oneOf(Object.values(DropdownMenuTheme)).isRequired,
  children: PropTypes.node,
  className: PropTypes.node,
};

DropdownTrigger.defaultProps = {
  children: undefined,
  className: "",
};

const CustomDropdownMenu = React.forwardRef(
  (
    { children, withClose, className, close },
    ref // eslint-disable-line no-unused-vars
  ) => (
    <div className={className}>
      {withClose && <CloseButton onClick={close} />}
      {children}
    </div>
  )
);

CustomDropdownMenu.propTypes = {
  children: PropTypes.node,
  withClose: PropTypes.bool,
  className: PropTypes.string,
  close: PropTypes.func.isRequired,
};

CustomDropdownMenu.defaultProps = {
  withClose: false,
  className: "",
  children: undefined,
};

export const DropdownMenu = ({
  theme,
  menuAlign,
  btnText,
  showArrow,
  className,
  menuClassName,
  children,
  withClose,
  showIcon,
  iconStyles,
  iconComponent,
  iconColor,
  hoverIconColor,
  isOpen,
  toggleOpen,
  disabled,
}) => {
  const [hovered, setHovered] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const containerRef = useRef(null);

  // Matches the original convention: passing `isOpen` puts the caller in
  // control (toggled via `toggleOpen`), otherwise this component manages
  // its own open/close state.
  const controlled = isOpen !== undefined;
  const show = controlled ? isOpen : internalOpen;

  const close = () => {
    if (controlled) {
      toggleOpen?.();
    } else {
      setInternalOpen(false);
    }
  };

  const handleToggleClick = (event) => {
    if (controlled) {
      toggleOpen?.(event);
    } else {
      setInternalOpen((prev) => !prev);
    }
  };

  // react-bootstrap's Dropdown handled outside-click/Escape-to-close
  // automatically - replicate that here since this no longer wraps it.
  useEffect(() => {
    if (!show) return undefined;

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        close();
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") close();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const toggleHover = () => {
    if (!disabled) {
      setHovered(!hovered);
    }
  };

  const renderIcon = (component) => {
    const IconComponent = component;
    return (
      <div style={iconStyles}>
        <IconComponent color={hovered ? hoverIconColor : iconColor} />
      </div>
    );
  };

  return (
    <div
      className={classnames("dropdown", show && "show", className)}
      ref={containerRef}
    >
      <DropdownTrigger
        theme={theme}
        arrowClass={showArrow ? `icon-arrow` : ""}
        onMouseEnter={toggleHover}
        onMouseLeave={toggleHover}
        onClick={handleToggleClick}
        disabled={disabled}
        aria-expanded={show}
      >
        {btnText} {showIcon ? renderIcon(iconComponent) : null}
      </DropdownTrigger>
      {show && (
        <CustomDropdownMenu
          className={classnames(
            "dropdown-menu",
            "show",
            menuClassName,
            `dropdown-menu-${menuAlign}`
          )}
          withClose={withClose}
          close={close}
        >
          {children}
        </CustomDropdownMenu>
      )}
    </div>
  );
};

DropdownMenu.propTypes = {
  theme: PropTypes.oneOf(Object.values(DropdownMenuTheme)).isRequired,
  menuAlign: PropTypes.oneOf(Object.values(DropdownMenuAlign)),
  btnText: PropTypes.string,
  showArrow: PropTypes.bool,
  children: PropTypes.node,
  // classNames for the dropdown trigger
  className: PropTypes.string,
  menuClassName: PropTypes.string,
  withClose: PropTypes.bool,
  showIcon: PropTypes.bool,
  iconStyles: PropTypes.shape({}),
  iconComponent: PropTypes.func,
  iconColor: PropTypes.string,
  hoverIconColor: PropTypes.string,
  isOpen: PropTypes.bool,
  toggleOpen: PropTypes.func,
  disabled: PropTypes.bool,
};

DropdownMenu.defaultProps = {
  menuAlign: DropdownMenuAlign.RIGHT,
  showArrow: false,
  children: undefined,
  className: "",
  menuClassName: "",
  btnText: "",
  withClose: false,
  showIcon: false,
  iconStyles: {},
  iconComponent: null,
  iconColor: "",
  hoverIconColor: "",
  // Setting `isOpen` to `undefined` means that the open state is controlled
  // INSIDE react-bootstrap's Dropdown compnent. So, if no `isOpen` is passed
  // as the prop, the Dropdown will control itself. If an `isOpen` IS passed
  // in, then the caller is responsible for controlling the dropdown (using
  // the `toggleOpen` prop).
  isOpen: undefined,
  toggleOpen: undefined,
  disabled: false,
};
